import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Selbstmessende Navigation: misst, ob die drei Kopfzeilen-Bloecke
 * [Logo+Wortmarke | Desktop-Links | Desktop-Actions] nebeneinander in die
 * tatsaechlich verfuegbare Innenbreite passen. Wenn nicht -> `collapsed = true`
 * (Hamburger-Menue). Ersetzt den festen Tailwind-`lg`-Breakpoint durch eine echte
 * Platzmessung, damit die Leiste NIE ueberlappt - unabhaengig von Viewport-Breite,
 * Schriftmetrik oder kuenftigen Textaenderungen.
 *
 * WARUM LOOP-/FLICKER-FREI:
 * Es werden ausschliesslich Groessen gemessen, die NICHT vom `collapsed`-State
 * abhaengen:
 *   - rowRef.clientWidth     verfuegbare Innenbreite (viewport-/max-w-gebunden,
 *                            aendert sich nie durch das Ein-/Ausklappen)
 *   - logoRef.scrollWidth    Logo+Wortmarke (immer im DOM)
 *   - actionsRef.scrollWidth Desktop-Actions-Wrapper (immer im DOM, >=sm)
 *   - linksWidthRef          intrinsische Breite der Desktop-Links: wird live
 *                            gemessen, solange sie sichtbar sind, und sonst aus
 *                            dem letzten bekannten Wert gelesen (Links sind
 *                            statisch). Damit ist die Messgroesse invariant zum
 *                            State -> der Fixpunkt ist eindeutig, kein Toggle-Loop.
 *
 * Zusaetzliche Sicherungen:
 *   - useLayoutEffect: Erstmessung SYNCHRON vor dem Paint -> kein sichtbarer Flash.
 *   - ResizeObserver + requestAnimationFrame-Debounce: bündelt Events pro Frame
 *     und schreibt nie synchron im RO-Callback -> keine
 *     "ResizeObserver loop completed"-Warnung.
 *   - setState nur bei echter Aenderung -> kein unnoetiger Re-Render.
 *   - document.fonts.ready: Re-Messung nach Webfont-Load (Schriftbreiten aendern sich).
 *   - Hysterese (collapseGap < expandGap): verhindert Zittern an der Umschaltgrenze.
 *   - Messpause bei offenem Hamburger-Menue: Der Scroll-Lock entfernt je nach
 *     Browser die vertikale Scrollbar und vergroessert dadurch kurzfristig die
 *     messbare Breite. Ohne Pause koennte das Menue an der Umschaltgrenze direkt
 *     wieder in den Desktop-Modus springen und sich selbst schliessen.
 *
 * @param {object}  opts
 * @param {number}  opts.collapseGap  Mindest-Restabstand (px) gesamt, unter dem
 *                                     eingeklappt wird (Einklapp-Schwelle).
 * @param {number}  opts.expandGap    Abstand (px) gesamt, ab dem wieder ausgeklappt
 *                                     wird (Ausklapp-Schwelle, > collapseGap).
 * @param {boolean} opts.frozen       Behaelt den aktuellen Zustand waehrend einer
 *                                     voruebergehenden Layoutaenderung bei.
 */
export function resolveCollapsedState({
  available,
  content,
  collapsed,
  frozen = false,
  collapseGap = 8,
  expandGap = 12,
}) {
  if (frozen) return collapsed;
  const threshold = collapsed ? expandGap : collapseGap;
  return available < content + threshold;
}

export function useNavCollapse({ collapseGap = 8, expandGap = 12, frozen = false } = {}) {
  const rowRef = useRef(null); // innere Flex-Zeile OHNE Padding -> clientWidth = Innenraum
  const logoRef = useRef(null); // Logo + Wortmarke
  const linksRef = useRef(null); // Desktop-Links (verschwinden bei collapsed)
  const actionsRef = useRef(null); // Desktop-Actions (Mitglied werden + Mein Bereich)

  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(false); // Spiegel fuer stabile measure()-Identitaet
  const frozenRef = useRef(frozen); // verhindert Re-Messung durch den Menue-Scroll-Lock
  const linksWidthRef = useRef(0); // letzte bekannte Links-Breite (Cache)
  const rafRef = useRef(0);

  // Ref synchron zum Render aktualisieren, damit bereits geplante rAF-/RO-Callbacks
  // nie mit einem veralteten Freeze-Zustand messen.
  frozenRef.current = frozen;

  const measure = useCallback(() => {
    if (frozenRef.current) return;

    const row = rowRef.current;
    const logo = logoRef.current;
    const actions = actionsRef.current;
    if (!row || !logo || !actions) return;

    // Links nur live messen, solange sie sichtbar (nicht eingeklappt) sind;
    // andernfalls den zuletzt bekannten Wert verwenden.
    if (!collapsedRef.current && linksRef.current) {
      const w = linksRef.current.scrollWidth;
      if (w > 0) linksWidthRef.current = w;
    }

    // Noch keine valide Links-Breite bekannt (Layout im ersten Pass noch nicht
    // stabil) -> NICHT mit 0 entscheiden (sonst faelschlich "passt locker").
    // Ein nachfolgender Trigger (RO-Initialcallback / fonts.ready / resize) misst
    // erneut, sobald die echte Breite vorliegt.
    if (linksWidthRef.current === 0) return;

    const available = row.clientWidth;
    const content = logo.scrollWidth + linksWidthRef.current + actions.scrollWidth;

    // Hysterese: eingeklappt erst wieder ausklappen, wenn klar Platz ist (expandGap);
    // ausgeklappt einklappen, sobald der Mindestabstand unterschritten wird (collapseGap).
    // (collapsedRef.current IMMER hier lesen, nie das state `collapsed` -> keine stale closure.)
    const next = resolveCollapsedState({
      available,
      content,
      collapsed: collapsedRef.current,
      frozen: frozenRef.current,
      collapseGap,
      expandGap,
    });

    if (next !== collapsedRef.current) {
      collapsedRef.current = next;
      setCollapsed(next);
    }
  }, [collapseGap, expandGap]);

  const scheduleMeasure = useCallback(() => {
    // cancel-and-reschedule: buendelt mehrere Events zu EINER Messung pro Frame und
    // kann - anders als ein blockierender Guard - nie dauerhaft haengen bleiben,
    // falls ein geplanter rAF (z.B. im Hintergrund-Tab) verspaetet ausgefuehrt wird.
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      measure();
    });
  }, [measure]);

  useLayoutEffect(() => {
    // 1) Erstmessung synchron vor dem Paint -> korrekter Initialzustand, kein Flash.
    measure();

    // 2) Laufende Messung an der tatsaechlichen Geometrie.
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(scheduleMeasure);
      if (rowRef.current) ro.observe(rowRef.current);
      if (logoRef.current) ro.observe(logoRef.current);
      if (actionsRef.current) ro.observe(actionsRef.current); // faengt async clubInfo-Breitenwechsel
    }
    // Zweiter, RO-unabhaengiger Pfad (Robustheit bei Viewport-/Orientierungswechsel).
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', scheduleMeasure);
      window.addEventListener('orientationchange', scheduleMeasure);
    }

    // 3) Nach Webfont-Load neu messen (Schriftmetrik aendert die Breiten).
    let cancelled = false;
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) scheduleMeasure();
      });
    }

    return () => {
      cancelled = true; // Guard: kein setState nach unmount
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      if (ro) ro.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', scheduleMeasure);
        window.removeEventListener('orientationchange', scheduleMeasure);
      }
    };
  }, [measure, scheduleMeasure]);

  // Nach Ende der Messpause den realen Viewport erneut bewerten. So werden
  // zwischenzeitliche Resize-/Orientierungswechsel unmittelbar nachgeholt.
  useEffect(() => {
    if (!frozen) scheduleMeasure();
  }, [frozen, scheduleMeasure]);

  // `remeasure` erlaubt dem Aufrufer, nach asynchronen Inhaltsaenderungen
  // (z.B. clubInfo/easyVereinUrl laedt nach -> Actions-Breite waechst) deterministisch
  // und RO-unabhaengig neu zu messen.
  return { collapsed, rowRef, logoRef, linksRef, actionsRef, remeasure: measure };
}

export default useNavCollapse;
